/**
 * Utility functions for formatting dates, times, and other common tasks
 */

import { formatMarketResolutionCountdown } from './marketResolutionDisplay.ts';

/**
 * Format a date as a relative time string (e.g. "2 hours ago")
 * @param date The date to format
 * @returns A string representing the relative time
 */
export function formatRelativeTime(date: Date | string): string {
  const targetDate = date instanceof Date ? date : new Date(date);
  const now = new Date();
  
  // Calculate the time difference in milliseconds
  const diffMs = now.getTime() - targetDate.getTime();
  
  // Convert to seconds, minutes, hours, days
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  // Format based on the appropriate time unit
  if (diffDays > 0) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  } else if (diffHours > 0) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  } else if (diffMins > 0) {
    return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  } else {
    return diffSecs <= 5 ? 'just now' : `${diffSecs} seconds ago`;
  }
}

/**
 * Format a date as a string with the local format
 * @param date The date to format
 * @returns A formatted date string
 */
export function formatDate(date: Date | string): string {
  const targetDate = date instanceof Date ? date : new Date(date);
  return targetDate.toLocaleString();
}

/**
 * Format a currency value (plain without settings)
 * @param amount The amount to format
 * @returns A formatted currency string with R as symbol
 * @deprecated Use formatCurrency from settings context instead
 */
export function formatCurrency(amount: number): string {
  return `R${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Calculate time left until a given date
 * @param resolutionDate The target date
 * @returns A human-readable string of time remaining
 */
export function calculateTimeLeft(resolutionDate: string): string {
  return formatMarketResolutionCountdown(resolutionDate);
}
